import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as pdfjs from 'pdfjs-dist';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { colors } from 'styles/theme/colors';
import { FilePreviewPdfScrollSyncArea } from './filePreview.styles';

const PAGE_ATTR = 'data-file-preview-pdf-page';

function usePdfDocument(file: File) {
  const [doc, setDoc] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    let disposed = false;
    let loadedDoc: PDFDocumentProxy | null = null;

    setDoc(null);
    setPageCount(0);

    void (async () => {
      try {
        const pdf = await pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) })
          .promise;
        if (disposed) {
          void pdf.destroy();
          return;
        }
        loadedDoc = pdf;
        setDoc(pdf);
        setPageCount(pdf.numPages);
      } catch {
        if (!disposed) {
          setDoc(null);
          setPageCount(0);
        }
      }
    })();

    return () => {
      disposed = true;
      if (loadedDoc) void loadedDoc.destroy();
    };
  }, [file]);

  return { doc, pageCount };
}

function useElementClientWidth<T extends HTMLElement>(
  ref: React.RefObject<T>,
  dependency?: unknown
) {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => setWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [ref, dependency]);

  return width;
}

/** One stacked canvas — kept minimal; parent owns pdf doc + width. */
const StackPdfPage = ({
  pdf,
  pageNumber,
  width,
  label,
}: {
  pdf: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  label: string;
}) => {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;

    let cancelled = false;
    let task: { cancel: () => void; promise: Promise<void> } | null = null;

    void (async () => {
      try {
        const page = await pdf.getPage(pageNumber);
        if (cancelled) return;

        const maxW = Math.max(1, width - 16);
        const base = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: maxW / base.width });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const dpr = window.devicePixelRatio || 1;
        const w = Math.floor(viewport.width);
        const h = Math.floor(viewport.height);
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        task = page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        });
        await task.promise;
      } catch {
        /* resize cancel */
      }
    })();
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [pdf, pageNumber, width]);

  return (
    <div
      {...{ [PAGE_ATTR]: String(pageNumber) }}
      style={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        flexShrink: 0,
        paddingBottom: 8,
      }}
    >
      <canvas
        ref={ref}
        style={{ display: 'block', maxWidth: '100%', backgroundColor: colors.white }}
        aria-label={`${label}, page ${pageNumber}`}
      />
    </div>
  );
};

export const FilePreviewPdfScrollSync = ({
  file,
  onPagerInfo,
}: {
  file: File;
  onPagerInfo?: (info: { currentPage: number; totalPages: number }) => void;
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const cb = useRef(onPagerInfo);
  cb.current = onPagerInfo;
  const { doc, pageCount: nPages } = usePdfDocument(file);
  const cw = useElementClientWidth(scrollRef, doc);

  useEffect(() => {
    if (nPages < 1) return;
    cb.current?.({ currentPage: 1, totalPages: nPages });
  }, [nPages]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || nPages < 1) return;

    let raf = 0;
    let retries = 0;

    const tick = () => {
      const nodes = el.querySelectorAll<HTMLElement>(`[${PAGE_ATTR}]`);
      if (!nodes.length && retries++ < 90) return requestAnimationFrame(schedule);
      retries = 0;
      const { top, height } = el.getBoundingClientRect();
      const mid = top + height / 2;
      let best = 1;
      let bestD = Infinity;
      nodes.forEach(node => {
        const idx = parseInt(node.getAttribute(PAGE_ATTR) || '', 10);
        if (!Number.isFinite(idx)) return;
        const r = node.getBoundingClientRect();
        const c = r.top + r.height / 2;
        const d = Math.abs(c - mid);
        if (d < bestD) {
          bestD = d;
          best = idx;
        }
      });
      cb.current?.({ currentPage: best, totalPages: nPages });
    };

    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(tick);
    };
    el.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    const ro = new ResizeObserver(schedule);
    ro.observe(el);
    requestAnimationFrame(() => requestAnimationFrame(schedule));
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      ro.disconnect();
    };
  }, [nPages, cw]);

  const ready = doc && nPages > 0 && cw > 0;

  return (
    <FilePreviewPdfScrollSyncArea ref={scrollRef} data-testid="file-preview-pdf-scroll-sync">
      {ready &&
        Array.from({ length: nPages }, (_, i) => (
          <StackPdfPage key={i + 1} pdf={doc} pageNumber={i + 1} width={cw} label={file.name} />
        ))}
    </FilePreviewPdfScrollSyncArea>
  );
};
